type Rgb = [number, number, number];

/** Parses a CSS color (hex or rgb/rgba) into normalized 0–1 RGB for Lottie. */
export const parseColorToRgb = (color: string): Rgb | null => {
  const value = color.trim();
  if (!value) return null;

  const hex = value.startsWith("#") ? value.slice(1) : null;
  if (hex && (hex.length === 3 || hex.length === 6 || hex.length === 8)) {
    const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return [r / 255, g / 255, b / 255];
  }

  const rgb = value.match(/rgba?\(([^)]+)\)/i);
  if (rgb) {
    const [r, g, b] = rgb[1].split(",").map((n) => parseFloat(n));
    if ([r, g, b].some(Number.isNaN)) return null;
    return [r / 255, g / 255, b / 255];
  }

  return null;
};

/** Returns a clone of the Lottie data with every static fill/effect color set to `rgb`. */
export const recolorLottie = <T>(data: T, [r, g, b]: Rgb): T => {
  const clone = JSON.parse(JSON.stringify(data));

  const walk = (node: any) => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== "object") return;

    // Shape fill: { ty: "fl", c: { a: 0, k: [r, g, b, a] } }
    if (node.ty === "fl" && node.c?.a === 0 && Array.isArray(node.c.k)) {
      node.c.k = [r, g, b, node.c.k[3] ?? 1];
    }
    // Fill-effect color: { ty: 2, nm: "Color", v: { a: 0, k: [r, g, b] } }
    if (node.ty === 2 && node.nm === "Color" && node.v?.a === 0 && Array.isArray(node.v.k)) {
      node.v.k = [r, g, b];
    }

    Object.values(node).forEach(walk);
  };

  walk(clone);
  return clone;
};
